import { CbAuthURLService } from './cb-auth-url.service';
import { CbBannerService } from './cb-banner.service';
import { LogFactory } from './cb-debug';
import { cdn } from './cb-envars';
import { CLIENTS_METADATA } from './cb-login-banner.metadata';
import * as i0 from "@angular/core";
import * as i1 from "./cb-auth-url.service";
import * as i2 from "./cb-banner.service";
var log = LogFactory('CbLoginBannerService', false);
export var defaultLogoURL = 'https://lf-master-project-logos-prod.s3.us-east-2.amazonaws.com/thelinuxfoundation-color.svg';
/**
 * @fileoverview CbLoginBannerService - Login Banner Management Service
 *
 * This service manages the display logic for login banners and logos across different
 * contexts in the Linux Foundation authentication system. It determines which banner
 * or logo to display based on URL parameters, client metadata, and application context.
 *
 * @class CbLoginBannerService
 * @description
 * The service supports multiple banner types and contexts:
 * - **App Banners**: Logos for specific applications (Jira, Confluence, Jenkins, etc.)
 * - **Project Logos**: Logos for Linux Foundation projects (pulled from S3 buckets)
 * - **Event Banners**: Cvent event-specific banners
 * - **Registration Banners**: Special banners for LF Registration flows
 * - **Meeting Banners**: Context for LFX Meeting flows
 * - **Default Banners**: Fallback when no specific context is found
 *
 * @workflow
 * 1. **URL Parameter Extraction**: Extracts parameters from URL, fragments, and tokens
 * 2. **Context Detection**: Determines the application context (app, project, event, etc.)
 * 3. **Banner Resolution**: Resolves appropriate banner/logo based on context
 * 4. **Image Management**: Handles image loading, fallbacks, and display states
 * 5. **Error Handling**: Provides fallback mechanisms when images fail to load
 *
 * @dependencies
 * - CbAuthURLService: URL parameter parsing and token management
 * - CbBannerService: Banner image retrieval from S3 buckets
 * - CLIENTS_METADATA: Static metadata mapping client names to configurations
 *
 * @example
 * ```typescript
 * // The service auto-initializes in the constructor
 * constructor(private loginBannerService: CbLoginBannerService) {}
 *
 * // Access banner state
 * const bannerType = this.loginBannerService.bannerType;
 * const appImages = this.loginBannerService.appImages;
 * const eventImages = this.loginBannerService.eventImages;
 * ```
 *
 * @author Linux Foundation
 * @since 1.0.0
 */
var CbLoginBannerService = /** @class */ (function () {
    /**
     * @constructor
     * @description Initializes the CbLoginBannerService with required dependencies
     * @param {CbAuthURLService} cbAuthURLService - Service for URL parameter extraction
     * @param {CbBannerService} cbBannerService - Service for banner image management
     */
    function CbLoginBannerService(cbAuthURLService, cbBannerService) {
        this.cbAuthURLService = cbAuthURLService;
        this.cbBannerService = cbBannerService;
        // === BANNER STATE PROPERTIES ===
        /** @description The current banner type being displayed */
        this.bannerType = 'default';
        /** @description The application name extracted from URL parameters */
        this.appName = '';
        /** @description The client ID extracted from URL parameters */
        this.clientId = '';
        // === CONTEXT PROPERTIES ===
        /** @description Project slug used for project-specific banners */
        this.project = '';
        // === CVENT EVENT PROPERTIES ===
        /** @description Event name for Cvent event banners */
        this.eventName = '';
        /** @description Array of event banner images with display states */
        this.eventImages = [];
        // === LF REGISTRATION PROPERTIES ===
        /** @description Zoom meeting ID for registration flows */
        this.zoomID = '';
        /** @description Registration or meeting ID */
        this.id = '';
        /** @description Type of registration (e.g., 'webinar', 'conference') */
        this.registrationType = '';
        /** @description Flag indicating if this is a registration flow */
        this.isRegistration = false;
        // === DISPLAY STATE PROPERTIES ===
        /** @description Flag indicating if banners are still loading */
        this.isLoading = true;
        /** @description Flag to display default banner when no specific context */
        this.displayDefault = false;
        /** @description Array of application-specific banner images */
        this.appImages = [];
        /** @description Metadata object containing client-specific configuration */
        this.appMetadata = null;
        /** @description Array of bottom banner images (usually project logos) */
        this.bottomImages = [];
        // === LOGO POSITIONING PROPERTIES ===
        /** @description Flag indicating if app logo should be displayed */
        this.isAppLogoActive = true;
        /** @description Flag indicating if project logo should be displayed */
        this.isProjectLogoActive = false;
        /** @description Flag to position app logo to the left */
        this.withAppLogotoLeft = false;
        // === MEETING PROPERTIES ===
        /** @description Flag indicating if this is a meeting context */
        this.isMeeting = false;
        /** @description Meeting name/topic */
        this.meetingName = '';
        /** @description Meeting description/agenda */
        this.meetingDescription = '';
        /** @description Meeting visibility setting ('public', 'private', etc.) */
        this.meetingVisibility = 'public';
        /** @description Unique meeting identifier */
        this.meetingID = '';
        /** @description Meeting type (e.g., 'recurring', 'one-time') */
        this.meetingType = '';
        /** @description Meeting date/start time */
        this.meetingDate = '';
        /** @description Invite ID for meeting invitations */
        this.inviteID = '';
        /** @description Name of the meeting registrant */
        this.registrantName = '';
        /** @description Flag indicating if there's a meeting-related error */
        this.meetingError = false;
        this.initValues();
    }
    /**
     * @method getPersistentParamsInURL
     * @description Extracts and processes URL parameters from multiple sources to determine banner context
     *
     * This method consolidates parameters from:
     * - URL query parameters
     * - Decoded JWT tokens
     * - Return-to URL parameters
     * - Auth0 configuration parameters
     * - URL fragments
     *
     * @returns {Object} Object containing all extracted parameters for banner determination
     * @returns {string} returns.appName - Application name
     * @returns {string} returns.eventName - Event name for Cvent banners
     * @returns {string} returns.project - Project slug
     * @returns {string} returns.id - Registration/meeting ID
     * @returns {string} returns.registrationType - Type of registration
     * @returns {string} returns.zoomID - Zoom meeting ID
     * @returns {string} returns.clientId - Client identifier
     * @returns {string} returns.meetingName - Meeting name
     * @returns {string} returns.meetingDescription - Meeting description
     * @returns {string} returns.meetingVisibility - Meeting visibility
     * @returns {string} returns.meetingID - Meeting ID
     * @returns {string} returns.meetingType - Meeting type
     * @returns {string} returns.meetingDate - Meeting date
     * @returns {string} returns.inviteID - Invite ID
     * @returns {string} returns.registrantName - Registrant name
     * @returns {boolean} returns.meetingError - Meeting error flag
     *
     * @example
     * ```typescript
     * const params = this.getPersistentParamsInURL();
     * console.log(params.appName); // 'CB Funding'
     * console.log(params.project); // 'cncf'
     * ```
     */
    CbLoginBannerService.prototype.getPersistentParamsInURL = function () {
        log('entered getPersistentParamsInURL');
        var urlParams = this.cbAuthURLService.getAllURLParams();
        var decodedParams = this.cbAuthURLService.getDecodedToken();
        var returnToParams = this.cbAuthURLService.getParamsFromReturnTo();
        var auth0params = this.cbAuthURLService.getAuth0ConfigParams();
        var fragments = this.cbAuthURLService.getValuesFromFragments(urlParams.fragment);
        this.appName =
            urlParams.clientName ||
                auth0params.title ||
                decodedParams.clientName ||
                returnToParams.clientName ||
                urlParams.client_name ||
                decodedParams.client_name ||
                returnToParams.client_name ||
                auth0params.app ||
                urlParams.app ||
                decodedParams.app ||
                returnToParams.app ||
                fragments.clientName ||
                '';
        this.clientId =
            urlParams.clientId ||
                decodedParams.clientId ||
                returnToParams.clientId ||
                urlParams.client_id ||
                decodedParams.client_id ||
                returnToParams.client_id ||
                // INFO: apps like Tnc Login , has client information in client property
                urlParams.client ||
                decodedParams.client ||
                returnToParams.client ||
                '';
        if (this.appName && Array.isArray(this.appName)) {
            this.appName = this.appName[0];
        }
        this.setAppMetadata();
        this.zoomID =
            auth0params.zoomID ||
                urlParams.zoomID ||
                returnToParams.zoomID ||
                auth0params.zoom_id ||
                urlParams.zoom_id ||
                returnToParams.zoom_id;
        this.id = auth0params.id || urlParams.id || returnToParams.id;
        if (this.id && Array.isArray(this.id)) {
            this.id = this.id[0];
        }
        this.registrationType =
            auth0params.registrationType ||
                auth0params.registration_type ||
                urlParams.registrationType ||
                urlParams.registration_type ||
                returnToParams.registrationType ||
                returnToParams.registration_type;
        if (this.registrationType && Array.isArray(this.registrationType)) {
            this.registrationType = this.registrationType[0];
        }
        var projectSlugFromMetadata = (this.appMetadata && this.appMetadata.projectSlug) || '';
        this.project = projectSlugFromMetadata || auth0params.project || urlParams.project || returnToParams.project;
        if (this.project && Array.isArray(this.project)) {
            this.project = this.project[0];
        }
        this.eventName =
            auth0params.eventName ||
                urlParams.eventName ||
                returnToParams.eventName ||
                auth0params.event_name ||
                urlParams.event_name ||
                returnToParams.event_name;
        if (this.eventName && Array.isArray(this.eventName)) {
            this.eventName = this.eventName[0];
        }
        this.setMeetingInfo(urlParams, fragments, returnToParams);
        return {
            appName: this.appName,
            eventName: this.eventName,
            project: this.project,
            id: this.id,
            registrationType: this.registrationType,
            zoomID: this.zoomID,
            clientId: this.clientId,
            meetingName: this.meetingName,
            meetingDescription: this.meetingDescription,
            meetingVisibility: this.meetingVisibility,
            meetingID: this.meetingID,
            meetingType: this.meetingType,
            meetingDate: this.meetingDate,
            inviteID: this.inviteID,
            registrantName: this.registrantName,
            meetingError: this.meetingError,
        };
    };
    /**
     * @method initValues
     * @description Initializes the service and determines which banner type to display
     *
     * This method implements the core banner resolution logic:
     * 1. Extracts URL parameters
     * 2. Determines if it's a registration flow
     * 3. Prioritizes banner types in order: Registration → Event → App → Project → Default
     * 4. Sets appropriate banner configuration
     *
     * @workflow
     * - **Registration Flow**: If appName contains 'lf registration' and has ID + registrationType
     * - **Event Flow**: If eventName is present (Cvent events)
     * - **App Flow**: If appName is present (specific applications)
     * - **Project Flow**: If project slug is present
     * - **Default Flow**: Fallback when no specific context is found
     *
     * @returns {void}
     *
     * @example
     * ```typescript
     * // Called automatically in constructor
     * // Sets bannerType, appImages, eventImages, etc. based on URL context
     * ```
     */
    CbLoginBannerService.prototype.initValues = function () {
        log('entered int values');
        this.getPersistentParamsInURL();
        this.isRegistration = this.appName.toLowerCase().includes('lf registration');
        // this.isMeeting = this.appName.toLowerCase().includes('lfx meeting');
        log('data ? ', {
            appName: this.appName,
            project: this.project,
            eventName: this.eventName,
            isRegistration: this.isRegistration,
            id: this.id,
            registrationType: this.registrationType,
            zoomID: this.zoomID,
            clientId: this.clientId,
            meetingName: this.meetingName,
            meetingDescription: this.meetingDescription,
            meetingVisibility: this.meetingVisibility,
            meetingID: this.meetingID,
            meetingType: this.meetingType,
            meetingDate: this.meetingDate,
            inviteID: this.inviteID,
            registrantName: this.registrantName,
            meetingError: this.meetingError,
        });
        // if (this.isMeeting) {
        //   return;
        // }
        if (this.isRegistration) {
            var RegistrationIDFound = this.id && this.registrationType;
            if (RegistrationIDFound) {
                // this.setRegistrationLogos();
                this.setBottomImages();
                return;
            }
            // info: Registration ID is not found
            this.isRegistration = false;
            this.setProjectLogo();
            return;
        }
        if (this.eventName) {
            this.setEventImage();
            return;
        }
        if (this.appName) {
            this.setAppLogoURL();
            return;
        }
        if (this.project) {
            this.setProjectLogo();
            return;
        }
        this.setDefaultBanner();
    };
    /**
     * @method setAppMetadata
     * @description Sets application metadata from the CLIENTS_METADATA configuration
     *
     * @returns {void}
     *
     * @example
     * ```typescript
     * // Sets this.appMetadata based on this.appName
     * // Used to get projectSlug, logoURL, contactURL, etc.
     * ```
     */
    CbLoginBannerService.prototype.setAppMetadata = function () {
        var medatada = CLIENTS_METADATA[this.appName];
        if (medatada) {
            this.appMetadata = medatada;
        }
    };
    CbLoginBannerService.prototype.setRegistrationLogos = function () { };
    /**
     * @method setBottomImages
     * @description Sets bottom banner images (usually project logos)
     *
     * @returns {Array} Array of image objects with URL and hide properties
     *
     * @example
     * ```typescript
     * const images = this.setBottomImages();
     * // Returns: [{ hide: false, URL: 'https://...' }]
     * ```
     */
    CbLoginBannerService.prototype.setBottomImages = function () {
        var images = this.getProjectLogoURL();
        var defaultImage = {
            hide: true,
            URL: defaultLogoURL,
        };
        if (images && images.length === 0) {
            images = [defaultImage];
        }
        this.bottomImages = images.concat();
        return images;
    };
    /**
     * @method setDefaultBanner
     * @description Sets the default banner state when no specific context is found
     *
     * @returns {void}
     *
     * @example
     * ```typescript
     * // Clears all banner arrays and sets displayDefault = true
     * ```
     */
    CbLoginBannerService.prototype.setDefaultBanner = function () {
        log('entered setDefaultBanner');
        this.eventName = '';
        this.appImages = [];
        this.isRegistration = false;
        this.displayDefault = true;
    };
    /**
     * @method setEventImage
     * @description Sets event-specific banner images for Cvent events
     *
     * @returns {void}
     *
     * @example
     * ```typescript
     * // Uses CbBannerService to fetch event images from S3
     * // Falls back to default banner if no images found
     * ```
     */
    CbLoginBannerService.prototype.setEventImage = function () {
        log('entered setEventImage');
        var eventImages = this.cbBannerService.GetCventImageURL(this.eventName) || [];
        this.eventImages = this.cbBannerService.GetPossibleBanners(eventImages);
        log('cvent images ? ', { images: this.eventImages });
        if (!this.eventImages || !this.eventImages.length) {
            this.setDefaultBanner();
        }
    };
    CbLoginBannerService.prototype.removeEventImage = function (index) {
        log('entered removeEventImage');
        this.eventImages.splice(index, 1);
        if (!this.eventImages.length) {
            this.setDefaultBanner();
        }
    };
    CbLoginBannerService.prototype.displayEventImage = function (banner) {
        log('entered displayEventImage', { banner: banner });
        // todo: once we have 1 loaded , hide others
        banner.hide = false;
    };
    /**
     * @method getAppLogoURL
     * @description Gets the appropriate logo URL for the current application
     *
     * Supports hardcoded mappings for common apps (Jira, Confluence, Jenkins, etc.)
     * and uses CLIENTS_METADATA for configured applications.
     *
     * @returns {Object|null} Image object with URL, hide, and logoCss properties, or null if no logo found
     * @returns {boolean} returns.hide - Whether the image should be hidden initially
     * @returns {string} returns.URL - The image URL
     * @returns {string} returns.logoCss - Additional CSS classes for the logo
     *
     * @example
     * ```typescript
     * const logo = this.getAppLogoURL();
     * // Returns: { hide: true, URL: 'https://cdn.../jira.svg', logoCss: '' }
     * ```
     */
    CbLoginBannerService.prototype.getAppLogoURL = function () {
        log('entered getAppLogoURL');
        var appName = this.appName.toLowerCase();
        log('app name', { appName: appName });
        var imageURL = '';
        var logoCss = '';
        if (appName.includes('jira')) {
            imageURL = cdn + '/assets/applogos/jira.svg';
            this.withAppLogotoLeft = true;
        }
        else if (appName.includes('confluence')) {
            imageURL = cdn + '/assets/applogos/confluence.svg';
            this.withAppLogotoLeft = true;
        }
        else if (appName.includes('wiki')) {
            imageURL = cdn + '/assets/applogos/confluence.svg';
            // imageURL = cdn + '/assets/applogos/wiki.svg';
            this.withAppLogotoLeft = true;
        }
        else if (appName.includes('jenkins')) {
            imageURL = cdn + '/assets/applogos/jenkins.svg';
            this.withAppLogotoLeft = true;
        }
        else if (appName.includes('nexus3')) {
            imageURL = cdn + '/assets/applogos/nexus3.svg';
            this.withAppLogotoLeft = true;
        }
        else {
            var appImage = CLIENTS_METADATA[this.appName];
            if (appImage && appImage.logoURL) {
                imageURL = cdn + "/assets/applogos/" + appImage.logoURL;
                logoCss = appImage.logoCss || '';
            }
        }
        log(' image ? ', { imageURL: imageURL });
        if (!imageURL) {
            return null;
        }
        return {
            hide: true,
            URL: imageURL,
            logoCss: logoCss,
        };
    };
    /**
     * @method getProjectLogoURL
     * @description Gets project logo URLs from multiple S3 bucket sources
     *
     * @returns {Array} Array of image objects with hide and URL properties
     *
     * @example
     * ```typescript
     * const logos = this.getProjectLogoURL();
     * // Returns: [{ hide: false, URL: 'https://lf-master-project-logos-prod.s3.amazonaws.com/cncf.svg' }]
     * ```
     */
    CbLoginBannerService.prototype.getProjectLogoURL = function () {
        log('entered getProjectLogoURL', { project: this.project });
        if (!this.project) {
            return [];
        }
        var projectName = this.project.toLowerCase();
        var buckets = [
            "https://lf-master-project-logos-prod.s3.amazonaws.com/" + projectName + ".svg",
            "https://lf-login-platform-assets-production.s3.amazonaws.com/project/" + projectName + ".svg",
        ];
        var images = buckets.map(function (item) {
            return {
                hide: false,
                URL: item,
            };
        });
        log('images ? ', { images: images });
        return images;
    };
    /**
     * @method setAppLogoURL
     * @description Sets the application logo and bottom images based on the appName
     *
     * @returns {void}
     *
     * @example
     * ```typescript
     * // Sets appImages to the result of getAppLogoURL()
     * // Sets bottomImages to the result of setBottomImages()
     * ```
     */
    CbLoginBannerService.prototype.setAppLogoURL = function () {
        log('entered setAppLogoURL');
        var appImage = this.getAppLogoURL();
        log('appImage ', { appImage: appImage });
        if (appImage) {
            this.appImages = [appImage];
            this.setBottomImages();
            return;
        }
        this.isAppLogoActive = false;
        this.isProjectLogoActive = true;
        this.setProjectLogo();
    };
    CbLoginBannerService.prototype.removeAppImage = function (index) {
        log('entered removeAppImage', { index: index });
        this.appImages.splice(index, 1);
        if (!this.appImages.length) {
            this.setDefaultBanner();
            return;
        }
    };
    CbLoginBannerService.prototype.displayAppImage = function (image) {
        log('enterd displayAppImage', { image: image });
        image.hide = false;
    };
    CbLoginBannerService.prototype.removePossibleImage = function (index, list) {
        log('entered removePossibleImage', { index: index });
        list.splice(index, 1);
        if (list.length === 0) {
            list = [
                {
                    hide: false,
                    URL: defaultLogoURL,
                },
            ];
        }
        // if (!list.length) {
        //   this.setDefaultBanner();
        //   return;
        // }
    };
    CbLoginBannerService.prototype.displayPossibleImage = function (image, list) {
        log('enterd displayPossibleImage', { image: image });
        // const isDefaultImage = list.findIndex(item => item.URL === image.URL) === list.length;
        var isOneActive = list.find(function (item) { return item.hide === false; });
        log('isOneActive ? ', { isOneActive: isOneActive });
        if (!isOneActive) {
            image.hide = false;
            return;
        }
        var isOneActiveDefault = isOneActive && isOneActive.URL === defaultLogoURL;
        log(' isOneActiveDefault ? ', { isOneActiveDefault: isOneActiveDefault });
        if (isOneActiveDefault) {
            list.forEach(function (item) {
                item.hide = true;
            });
            image.hide = false;
        }
    };
    /**
     * @method setProjectLogo
     * @description Sets the project logo and bottom images based on the project slug
     *
     * @returns {void}
     *
     * @example
     * ```typescript
     * // Sets appImages to the result of getProjectLogoURL()
     * // Sets bottomImages to the result of setBottomImages()
     * ```
     */
    CbLoginBannerService.prototype.setProjectLogo = function () {
        log('enterd setProjectLogo');
        var projectImages = this.getProjectLogoURL();
        log('projectImages ? ', { projectImages: projectImages });
        this.appImages = projectImages;
        if (!projectImages.length) {
            this.setDefaultBanner();
        }
    };
    /**
     * @method setMeetingInfo
     * @description Extracts and sets meeting-related information from URL parameters
     *
     * @param {Object} urlParams - URL parameters object
     * @param {Object} fragments - URL fragments object
     * @param {Object} returnToParams - Return-to parameters object
     * @returns {void}
     *
     * @example
     * ```typescript
     * // Sets meetingName, meetingDescription, meetingVisibility, etc.
     * // from various parameter sources
     * ```
     */
    CbLoginBannerService.prototype.setMeetingInfo = function (urlParams, fragments, returnToParams) {
        if (returnToParams.fragment) {
            fragments = this.cbAuthURLService.getValuesFromFragments(returnToParams.fragment);
        }
        log('meeting values ? ', { urlParams: urlParams, fragments: fragments, returnToParams: returnToParams });
        this.meetingID = urlParams.meetingID || fragments.meetingID || returnToParams.meetingID;
        if (urlParams.topic || fragments.topic || returnToParams.topic) {
            this.meetingName = urlParams.topic || fragments.topic || returnToParams.topic;
            this.meetingDescription = urlParams.agenda || fragments.agenda || returnToParams.agenda;
            this.meetingVisibility = urlParams.visibility || fragments.visibility || returnToParams.visibility;
            this.meetingType = urlParams.recurring || fragments.recurring || returnToParams.recurring;
            this.meetingDate = urlParams.start_time || fragments.start_time || returnToParams.start_time;
            this.inviteID = urlParams.inviteID || fragments.inviteID || returnToParams.inviteID;
            this.registrantName = urlParams.firstName
                ? urlParams.firstName + " " + urlParams.lastName
                : fragments.firstName
                    ? fragments.firstName + " " + fragments.lastName
                    : returnToParams.firstName + " " + returnToParams.lastName;
        }
        if (urlParams.error || fragments.error || returnToParams.error) {
            this.meetingError = true;
        }
    };
    CbLoginBannerService.ngInjectableDef = i0.defineInjectable({ factory: function CbLoginBannerService_Factory() { return new CbLoginBannerService(i0.inject(i1.CbAuthURLService), i0.inject(i2.CbBannerService)); }, token: CbLoginBannerService, providedIn: "root" });
    return CbLoginBannerService;
}());
export { CbLoginBannerService };
